import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useEnableGcpAccount } from "featureFlags/hooks/useEnableGcpAccount";
import { useDatabasesNames } from "services/databases/useDatabasesNames";
import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";

import { useAccessManager } from "components/App/accessManager";
import Button, { ButtonTemplate } from "components/Button";
import { Tree } from "components/Tree/Tree";
import { ItemDataType, TreeNodeType } from "components/Tree/types";

import { useCurrentAccount } from "../../Account/useCurrentAccount";
import { useCreateDatabase } from "../CreateMenu/useCreateDatabase";
import { useLoadData } from "../CreateMenu/useLoadData";
import { EmptySearch } from "../EmptyState/EmptySearch";
import { DatabaseIcon, EmptyState } from "../EmptyState/EmptyState";
import { Panel } from "../Panel";
import { Search } from "../Search";
import { useLeftSidebarContext } from "../context";
import {
  ColumnLabel,
  DatabaseLabel,
  DefaultLabel,
  IndexLabel,
  InformationSchemaTableLabel,
  TableLabel,
  ViewLabel,
} from "./Labels";
import { useChildren } from "./useChildren";
import { useDeleteDatabase } from "./useDeleteDatabase";
import { useEditDatabase } from "./useEditDatabase";

import styles from "../styles.module.scss";

const EmptyLabel = () => {
  return <div className={styles.empty}>No objects found.</div>;
};

const labelMap = {
  database: DatabaseLabel,
  table: TableLabel,
  view: ViewLabel,
  external_table: TableLabel,

  information_schema_table: InformationSchemaTableLabel,
  index: IndexLabel,
  column: ColumnLabel,
  information_schema_column: ColumnLabel,
  external_table_column: ColumnLabel,
  view_column: ColumnLabel,
  empty: EmptyLabel,
};

export const DatabasesPanel = React.memo(() => {
  const { t } = useTranslation();
  const { createDatabaseMarkup, openCreateDatabase } = useCreateDatabase();
  const databases = useDatabasesNames({
    showSystemDatabases: true,
  });
  const {
    onDatabaseSearchChange,
    databaseSearchState,
    onExpand,
    expandedItems,
    treeData,
  } = useLeftSidebarContext();

  const { isAllowedTo } = useAccessManager();
  const enabledGcpAccount = useEnableGcpAccount();
  const { deleteDatabase, deleteDatabaseMarkup } = useDeleteDatabase();
  const { openEditDatabase, editDatabaseMarkup } = useEditDatabase();
  const { openLoadData, loadDataMarkup } = useLoadData();
  const { getAccount } = useCurrentAccount();

  const accountName = getAccount().accountName;

  const data = useMemo(() => {
    const databasesMap = new Map<string, ItemDataType>(
      treeData.map(item => [item.databaseName as string, item])
    );
    return databases.map(database => {
      const { catalogName } = database;
      const children = databasesMap.get(catalogName)?.children || [];
      return {
        type: "database",
        label: catalogName,
        value: catalogName,
        payload: database,
        databaseName: catalogName,
        children,
      };
    });
  }, [databases, treeData]);

  const getChildren = useChildren();

  const actions = useMemo(() => {
    return {
      deleteDatabase,
      openLoadData,
      openEditDatabase,
    };
  }, [deleteDatabase, openLoadData, openEditDatabase]);

  const renderLabel = useCallback(
    (node: TreeNodeType) => {
      const { type } = node;
      const Label = labelMap[type as keyof typeof labelMap] || DefaultLabel;
      return (
        <Label
          node={node}
          actions={actions}
        />
      );
    },
    [actions]
  );

  const allowedToCreateDatabase =
    isAllowedTo(
      PrivilegeObjectType.account,
      accountName,
      SqlRbacAction.DATABASE_CREATE
    ) && !enabledGcpAccount;

  if (!databases.length) {
    const loadDataBtn = (
      <Button
        dataTestid="db-empty-list-load-data-button"
        text={t("workspace.create_menu.load_data")}
        template={ButtonTemplate.Primary}
        onClick={() => openLoadData()}
      />
    );
    return (
      <>
        <Search
          onChange={onDatabaseSearchChange}
          value={databaseSearchState}
          testId="databases-search-input"
          disabled
        />
        <EmptyState
          icon={<DatabaseIcon />}
          title={t("workspace.databases.empty")}
          subtitle={
            allowedToCreateDatabase
              ? t("workspace.databases.empty_subtitle")
              : undefined
          }
          button={
            allowedToCreateDatabase
              ? t("workspace.databases.empty_button")
              : undefined
          }
          buttonTemplate={ButtonTemplate.Ghost}
          content={allowedToCreateDatabase ? loadDataBtn : undefined}
          onClick={openCreateDatabase}
          testId="databases-empty-list"
        />
        {createDatabaseMarkup}
        {loadDataMarkup}
      </>
    );
  }

  return (
    <>
      <Search
        onChange={onDatabaseSearchChange}
        value={databaseSearchState}
        testId="databases-search-input"
      />
      <Panel testId="databases-list-panel">
        <Tree
          controlledExpandedItems={expandedItems}
          onExpand={onExpand}
          data={data}
          getChildren={getChildren}
          renderLabel={renderLabel}
          searchKeyword={databaseSearchState}
          nodeClassName={styles.node}
          treeClassName={styles.tree}
          emptySearchState={
            <EmptySearch
              search={databaseSearchState}
              testId="empty-databases-search"
            />
          }
          emptyState={
            <EmptyState
              icon={<DatabaseIcon />}
              title={t("workspace.databases.empty")}
              subtitle={
                allowedToCreateDatabase
                  ? t("workspace.databases.empty_subtitle")
                  : undefined
              }
              button={
                allowedToCreateDatabase
                  ? t("workspace.databases.empty_button")
                  : undefined
              }
              onClick={openCreateDatabase}
              testId="databases-empty-list"
            />
          }
        />
        {deleteDatabaseMarkup}
        {editDatabaseMarkup}
        {loadDataMarkup}
      </Panel>
    </>
  );
});
